import { logger } from 'lib/logger';
import { weblinksIncomingApiCall } from 'reactApp/api/weblinks/WeblinksIncomingApiCall';
import { weblinksIncomingRemoveApiCall } from 'reactApp/api/weblinks/WeblinksIncomingRemoveApiCall';
import { IS_ALL_DOC_SHARED_SORT_ENABLED } from 'reactApp/appHelpers/featuresHelpers/features/alldocumentsSortEnabled';
import { renderRemoveDialog } from 'reactApp/components/RemoveDialog/RemoveDialog.helpers';
import { getAllDocumentsExtensions } from 'reactApp/modules/allDocuments/allDocuments.selectors';
import { getIncomingPublicCategory, getIncomingPublicCursor } from 'reactApp/modules/incomingPublic/incomingPublic.selectors';
import { removeFileSuccess } from 'reactApp/modules/modifying/modifying.actions';
import { getSortById } from 'reactApp/modules/sort/sort.selectors';
import { ESortTypes } from 'reactApp/modules/sort/sort.types';
import { sendXray } from 'reactApp/utils/ga';
import { captureException } from 'reactApp/utils/tracer';
import { channel } from 'redux-saga';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';

import { getLoadMoreLimit } from '../storage/storage.helpers';
import { EStorageType } from '../storage/storage.types';
import {
    loadIncomingPublicRequest,
    loadIncomingPublicSuccess,
    loadMoreIncomingPublicRequest,
    removeIncomingPublicRequest,
} from './incomingPublic.module';
import { type LoadIncomingPublicOpts, type WeblinksIncomingRemoveParams, EIncomingPublicCategory } from './incomingPublic.types';

const LOAD_LIMIT = getLoadMoreLimit(EStorageType.sharedIncoming);
const REMOVE_PAYLOAD_LIMIT = 300;

const getIncoming = async ({ limit = LOAD_LIMIT, cursor = '', exts = '', sort = undefined }: LoadIncomingPublicOpts) => {
    const replacedMtimeSort = sort?.type ? (sort.type === ESortTypes.mtime ? 'time' : sort.type) : undefined;
    return (await weblinksIncomingApiCall({ limit, cursor, exts, sort: replacedMtimeSort, order: sort?.order })).data;
};

const removeIncomingPublic = async (params: WeblinksIncomingRemoveParams) => (await weblinksIncomingRemoveApiCall(params)).data;

const sendLogsOnError = (haveSort: boolean, type: string, error, category: EIncomingPublicCategory) => {
    logger.error(error);
    if (haveSort) {
        sendXray(`alldocuments_error_sort_incoming-public_${type}_${error.status ?? 'other'}`, null, { dwh: { category } });
    }
    sendXray('incoming-public-load-error', null, { dwh: { category } });
    captureException(error, { issueKey: 'Incoming public load error' });
};

export function* handleLoadIncomingPublic({ payload }: ReturnType<typeof loadIncomingPublicRequest>) {
    const sort = yield select((state) => getSortById(state, EStorageType.incomingPublic));
    const haveSort = IS_ALL_DOC_SHARED_SORT_ENABLED ? sort : undefined;

    try {
        let extensions: string[] = [];
        if (payload.category === EIncomingPublicCategory.documents) {
            extensions = yield select(getAllDocumentsExtensions);
        }

        const data = yield call(getIncoming, { limit: LOAD_LIMIT, cursor: '', exts: extensions.join(','), sort: haveSort });
        yield put(loadIncomingPublicSuccess(data));
    } catch (error) {
        sendLogsOnError(haveSort, sort.type, error, payload.category);
    }
}

function* handleLoadMoreIncomingPublic({ payload }: ReturnType<typeof loadMoreIncomingPublicRequest>) {
    const cursor = yield select(getIncomingPublicCursor);
    const sort = yield select((state) => getSortById(state, EStorageType.incomingPublic));
    const haveSort = IS_ALL_DOC_SHARED_SORT_ENABLED ? sort : undefined;

    let extensions: string[] = [];
    if (payload.category === EIncomingPublicCategory.documents) {
        extensions = yield select(getAllDocumentsExtensions);
    }

    try {
        const data = yield call(getIncoming, {
            limit: LOAD_LIMIT,
            cursor,
            exts: extensions.join(','),
            sort: haveSort,
        });

        yield put(loadIncomingPublicSuccess(data));
    } catch (error) {
        sendLogsOnError(haveSort, sort.type, error, payload.category);
    }
}

function* handleIncomingPublicRemove({ payload }: ReturnType<typeof removeIncomingPublicRequest>) {
    const incomingPublicCategory = yield select(getIncomingPublicCategory);
    try {
        const deleteChannel = channel();

        const dialog = renderRemoveDialog({
            items: payload.weblink_ids,
            onClose: () => {
                deleteChannel.close();
            },
            onRemove: () => {
                deleteChannel.put(true);
            },
        });

        yield take(deleteChannel);
        yield deleteChannel.close();
        if (dialog) {
            yield call(dialog.closeDialog);
        }

        if (!payload.weblink_ids) {
            return;
        }

        for (let i = 0; i < payload.weblink_ids.length; i += REMOVE_PAYLOAD_LIMIT) {
            const chunk = payload.weblink_ids.slice(i, i + REMOVE_PAYLOAD_LIMIT);
            yield call(removeIncomingPublic, {
                weblink_ids: chunk,
            });
        }

        yield put(removeFileSuccess({ ids: payload.weblink_ids }));

        yield call(sendXray, 'incoming-public-remove-success', null, { dwh: { category: incomingPublicCategory } });
    } catch (error) {
        logger.error(error);
        sendXray('incoming-public-remove-error', null, { dwh: { category: incomingPublicCategory } });
        captureException(error, { issueKey: 'Incoming public remove error' });
    }
}

export function* watchIncomingPublic() {
    yield takeEvery(loadIncomingPublicRequest.toString(), handleLoadIncomingPublic);
    yield takeEvery(loadMoreIncomingPublicRequest.toString(), handleLoadMoreIncomingPublic);
    yield takeEvery(removeIncomingPublicRequest.toString(), handleIncomingPublicRemove);
}
